import { eip6963Connector } from "./connectors";
import { readThisTabRdns } from "./selectedWalletRecord";

// Re-exported so the record's writers and its reader stay discoverable from one
// place, even though they live in a leaf module with no connector imports.
export {
  forgetSelectedWallet,
  markSelectedWalletForThisTab,
  rememberSelectedWallet,
} from "./selectedWalletRecord";

/**
 * The wallet the user chose is no longer attached.
 *
 * Named rather than generic so callers can tell it apart from "no wallet at
 * all": the remedy is to reconnect that wallet, not to connect a first one.
 *
 * Its wording reaches the user on the register path (registerErrorMessage) and
 * the order path (isOwnOrderError passes it through untouched). It does NOT yet
 * reach the deposit/withdraw modals, which classify only user-rejection and
 * insufficient-gas and drop everything else into "Deposit failed" --
 * DepositModal.submit.tsx and WithdrawModal.helpers.ts. Stated rather than
 * implied, because the earlier version of this comment claimed all three.
 */
export class SelectedWalletUnavailableError extends Error {
  readonly rdns: string;

  constructor(rdns: string) {
    super(
      `The wallet you connected with (${rdns}) is no longer available. ` +
        "Reconnect it and try again."
    );
    this.name = "SelectedWalletUnavailableError";
    this.rdns = rdns;
  }
}

/**
 * The EIP-1193 provider to build an ethers `Web3Provider` from for signing and
 * transactions.
 *
 * Historically the app read `window.ethereum` directly, which only exists for
 * injected browser wallets. SDK wallets (Privy/Para/DogeOS embedded) are NOT
 * injected — there is no `window.ethereum` — so `new Web3Provider(undefined)`
 * yields a provider whose `getSigner()` throws "missing provider".
 *
 * The singleton `EIP1193Connector` holds whichever provider is actually live —
 * `adopt()` wires up SDK wallets and `setProvider()` wires up EIP-6963 ones, and
 * both land in the same field — so it is the source of truth on every chain.
 * `window.ethereum` remains only as a last resort for a session that predates
 * either path.
 */
export function getActiveEip1193(): any {
  // Prefer the connector's provider on EVERY chain, not just SDK ones.
  //
  // `adopt()` and `setProvider()` both populate this field, so it is set for SDK
  // sessions AND for EIP-6963 injected sessions. Gating on the chain kind meant a
  // wallet chosen through EIP-6963 was ignored whenever the chain was WEB3REACT,
  // and `window.ethereum` was used instead — which for anything other than the
  // default injected wallet (Rabby, Trust, OKX, or a second MetaMask-like
  // extension) is a DIFFERENT wallet. Signing with it makes `msg.sender` differ
  // from `trader` and the perp contracts revert `NotTraderOrOperator`; see the
  // note in hooks/api/order/usePerpOrder.ts.
  const bridged = (eip6963Connector as any).provider;
  if (bridged && typeof bridged.request === "function") {
    return bridged;
  }

  // The selected wallet is gone, so REFUSE rather than guess.
  //
  // EIP-6963 exists because "browser extensions are loaded in an unpredictable
  // and unstable order" and "the last wallet to load usually wins"
  // `window.ethereum`. The spec is explicit that dapps "SHOULD disable
  // window.ethereum usage except as a fail-over when discovery fails" -- a
  // fail-over for NO selection, not a substitute for one that went missing.
  //
  // Guessing here is what produced three separate bug reports -- "unknown
  // account #0", `NotTraderOrOperator` reverts, and "Cannot read market lot
  // size" -- plus a 4100 on sign-in. Each presented as a different bug because a
  // wrong wallet fails differently every time.
  //
  // WHAT ACTUALLY REACHES THIS. A failed eager reconnect: `useEagerConnect` marks
  // the tab from the durable record, then finds no matching announcement --
  // the wallet was uninstalled or disabled between sessions -- so it never calls
  // `setProvider` and the connector stays empty. That is the case worth a
  // sentence instead of a silent substitution.
  //
  // It is NOT reached by a rejected connect or a failed SDK bridge, though an
  // earlier version of this comment claimed both: those go through `activate()`,
  // which clears the record on entry, so they fail over rather than refuse. Being
  // precise matters here, because the set of paths that reach a refusal is
  // exactly the set of ways it can fire wrongly.
  //
  // Read per-tab, never from the shared store -- see the note on the two stores.
  const selectedRdns = readThisTabRdns();
  if (selectedRdns) {
    throw new SelectedWalletUnavailableError(selectedRdns);
  }

  // Nothing was ever selected: this is the fail-over the spec sanctions.
  return (window as any).ethereum;
}

/**
 * The same provider, for READ-ONLY use, which does not refuse.
 *
 * The refusal above exists to stop the app *acting* through a wallet nobody
 * chose -- signing, or sending a transaction whose `msg.sender` would be wrong.
 * An `eth_call` has neither: the provider is just an RPC endpoint, and any
 * endpoint on the right chain returns the same answer. So refusing a read buys
 * no safety and costs the caller its result.
 *
 * Only `readProvider` needs this, and only when a chain has no RPC configured --
 * documented there as "better a degraded read than none". Keeping that promise
 * is the whole point: over-refusing is its own bug, and it would land on exactly
 * the misconfigured chain that can least afford another failure.
 */
export function getActiveEip1193ForRead(): any {
  try {
    return getActiveEip1193();
  } catch (error) {
    if (error instanceof SelectedWalletUnavailableError) {
      return (window as any).ethereum;
    }
    throw error;
  }
}
